import React, { useEffect, useMemo, useState, useCallback } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { LuMessageSquarePlus } from "react-icons/lu";
import { FaInbox, FaPaperPlane, FaUsers } from "react-icons/fa";

import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "../../../hooks/useScollToTop";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import ScrollIndicator from "../../Home/ScrollIndicator";

import MessagesReceived from "./MessagesReceived";
import MessagesSent from "./MessagesSent";
import GroupMessages from "./GroupMessages";
import CreateMessageForm from "./CreateMessageForm";
import MessagingHero from "./MessagingHero";
import MessagingTabs from "./MessagingTabs";
import MessagingPanelShell from "./MessagingPanelShell";

const normalizeRole = (value = "") =>
  String(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "");

export default function Messaging() {
  useScrollToTop();

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const userId = user?.userId || user?.id;

  const normalizedRole = normalizeRole(userRole);
  const isAdminOrTeacher =
    normalizedRole.includes("admin") ||
    normalizedRole.includes("enseignant") ||
    Number(user?.role_id) === 1 ||
    Number(user?.role_id) === 3;

  const [active, setActive] = useState("received");
  const [showForm, setShowForm] = useState(false);
  const [refreshMessages, setRefreshMessages] = useState(false);

  const [counts, setCounts] = useState({
    received: { total: 0, unread: 0 },
    sent: { total: 0 },
    group: { total: 0 },
  });

  const setModuleCounts = useCallback((key, obj) => {
    setCounts((current) => ({
      ...current,
      [key]: {
        ...current[key],
        ...obj,
      },
    }));
  }, []);

  const handleCountsReceived = useCallback(
    (obj) => setModuleCounts("received", obj),
    [setModuleCounts]
  );

  const handleCountsSent = useCallback(
    (obj) => setModuleCounts("sent", obj),
    [setModuleCounts]
  );

  const handleCountsGroup = useCallback(
    (obj) => setModuleCounts("group", obj),
    [setModuleCounts]
  );

  const modules = useMemo(
    () =>
      [
        {
          id: "received",
          title: "Reçus",
          subtitle: "Boîte de réception",
          icon: FaInbox,
          badge: counts.received.unread || 0,
          badgeTone: "violet",
        },
        {
          id: "sent",
          title: "Envoyés",
          subtitle: "Historique",
          icon: FaPaperPlane,
          badge: counts.sent.total || 0,
          badgeTone: "sky",
        },
        isAdminOrTeacher && {
          id: "group",
          title: "Groupé",
          subtitle: "Groupes",
          icon: FaUsers,
          badge: counts.group.total || 0,
          badgeTone: "emerald",
        },
      ].filter(Boolean),
    [counts, isAdminOrTeacher]
  );

  const activeModule = modules.find((module) => module.id === active);

  const handleNewMessage = () => setShowForm(true);

  const handleSuccess = () => {
    setShowForm(false);
    setRefreshMessages(true);
  };

  useEffect(() => {
    if (!userId) {
      toast.error("Vous devez être connecté pour accéder à la messagerie.");
    }
  }, [userId]);

  return (
    <section className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-white">
      <ScrollIndicator />

      <div className="pt-[96px] sm:pt-[120px] lg:pt-[160px]" aria-hidden />

      <main className="mx-auto w-full max-w-7xl px-3 pb-28 sm:px-6 lg:px-8">
        <HeaderTitle
          role="Utilisateur"
          title="Messagerie"
          subtitle="Consulte tes messages, réponds rapidement et envoie des informations individuelles ou groupées selon ton rôle."
          icon={FaInbox}
          align="left"
          className="mb-4"
        />

        <div className="mb-4 sm:mb-7">
          <DashboardNavigation role={userRole} />
        </div>

        <MessagingHero
          unreadCount={counts.received.unread}
          receivedCount={counts.received.total}
          sentCount={counts.sent.total}
          canSendGroup={isAdminOrTeacher}
          onNewMessage={handleNewMessage}
        />

        <div className="sticky top-[76px] z-30 mt-4 rounded-[1.5rem] border border-white/70 bg-white/90 p-2 shadow-lg shadow-slate-200/70 backdrop-blur dark:border-slate-800/80 dark:bg-slate-950/90 dark:shadow-black/20 sm:top-[90px]">
          <MessagingTabs
            items={modules}
            activeId={active}
            onChange={setActive}
          />
        </div>

        <div className="mt-4 overflow-hidden rounded-[2rem] border border-white/70 bg-white/85 shadow-xl shadow-slate-200/70 backdrop-blur dark:border-slate-800/80 dark:bg-slate-900/70 dark:shadow-black/20 sm:mt-6">
          <MessagingPanelShell
            icon={activeModule?.icon}
            title={activeModule?.title}
            subtitle={activeModule?.subtitle}
            toolbar={
              <button
                type="button"
                onClick={handleNewMessage}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 text-sm font-bold text-white shadow-lg shadow-slate-300/50 transition hover:-translate-y-0.5 hover:shadow-xl dark:bg-white dark:text-slate-950 dark:shadow-black/20"
              >
                <LuMessageSquarePlus />
                Nouveau
              </button>
            }
          >
            {active === "received" && (
              <MessagesReceived
                userId={userId}
                refresh={refreshMessages}
                setRefresh={setRefreshMessages}
                onCounts={handleCountsReceived}
              />
            )}

            {active === "sent" && (
              <MessagesSent
                userId={userId}
                refresh={refreshMessages}
                setRefresh={setRefreshMessages}
                onCounts={handleCountsSent}
              />
            )}

            {active === "group" && isAdminOrTeacher && (
              <GroupMessages userId={userId} onCounts={handleCountsGroup} />
            )}
          </MessagingPanelShell>
        </div>
      </main>

      <button
        type="button"
        onClick={handleNewMessage}
        className="fixed bottom-5 right-4 z-40 inline-flex h-14 w-14 items-center justify-center rounded-full bg-violet-600 text-white shadow-2xl shadow-violet-500/30 transition hover:-translate-y-0.5 hover:bg-violet-700 sm:hidden"
        aria-label="Nouveau message"
      >
        <LuMessageSquarePlus className="text-2xl" />
      </button>

      <CreateMessageForm
        userId={userId}
        userRole={normalizedRole.includes("utilisateur") ? "user" : userRole}
        onSuccess={handleSuccess}
        show={showForm}
        onHide={() => setShowForm(false)}
      />

      <ToastContainer />
    </section>
  );
}